import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { getAnswerBatched } from 'api/answerBatchFetch'
import { Answer } from 'types/answers'
import { getCachedAnswer, isViewerHydratedAnswerCacheEntry } from 'state/cache'
import { ANSWER_ENTITY_QUERY_POLICY } from 'api/queryPolicies'

type Options = {
    cacheOnly?: boolean
}

export default function useGetAnswer(id: string, options: Options = {}) {
    const initialCachedAnswer = getCachedAnswer(id)
    const cachedAnswerNeedsHydration =
        initialCachedAnswer != null && !isViewerHydratedAnswerCacheEntry(initialCachedAnswer)
    //console.log('use get answer use get answerrrrrrrrrrrrrr', id, cachedAnswer)

    const result = useQuery({
        queryKey: ['answer', id],
        queryFn: async () => {
            // The natural-thread prefetch observer warms the entity cache before its row
            // mounts. Re-check at execution time so React Query does not duplicate that
            // request using the compact thread stub captured during render.
            const prefetchedAnswer = getCachedAnswer(id)
            if (isViewerHydratedAnswerCacheEntry(prefetchedAnswer)) {
                return prefetchedAnswer as Answer
            }
            // Coalesced with other same-tick answer fetches into one bulk call;
            // caches the answer as a side effect (like api.getAnswer callers did).
            const answer = await getAnswerBatched(id)
            if (!answer) {
                throw Object.assign(new Error('Answer unavailable'), { status: 404 })
            }
            return answer
        },

        enabled: !options.cacheOnly && !!id && !initialCachedAnswer?.is_draft,
        staleTime: ANSWER_ENTITY_QUERY_POLICY.staleTime,
        gcTime: ANSWER_ENTITY_QUERY_POLICY.gcTime,
        retry: (failureCount, error) =>
            Number((error as { status?: number })?.status) !== 404 && failureCount < 2,
        // Compact thread stubs intentionally share the Answer entity query key. They carry
        // layout metadata, not renderable content, so their fresh query timestamp must not
        // suppress hydration when a natural-flow row mounts.
        refetchOnMount: cachedAnswerNeedsHydration ? 'always' : false,
        refetchOnWindowFocus: result => result.state.status === 'error',
        refetchOnReconnect: true,
        networkMode: 'always',
    })

    const cachedAnswer = useMemo(() => getCachedAnswer(id), [id, result.data])

    return {
        answer: cachedAnswer || result.data,
        isError: result.isError,
        isFetching: result.isFetching,
    }
}
